import { useEffect, useRef, useState } from 'react';
import { api, type Wallet } from '../lib/api';
import { useApp } from '../store';

/**
 * Compte à rebours local des charges + regen optimiste à 0.
 * Évite le bug d’affichage bloqué à 0:00 quand api.me() renvoie encore msUntilNext≈0
 * et que le garde sync empêche de relancer l’effet.
 */
export function useWalletChargeClock(): number | null {
  const { wallet, setWallet, setZones } = useApp();
  const [now, setNow] = useState(() => Date.now());
  const baseMs = useRef<number | null>(wallet?.msUntilNext ?? null);
  const baseAt = useRef(Date.now());
  const syncingRef = useRef(false);
  const lastAppliedRegenAt = useRef(0);

  useEffect(() => {
    baseMs.current = wallet?.msUntilNext ?? null;
    baseAt.current = Date.now();
    setNow(Date.now());
  }, [wallet?.msUntilNext, wallet?.charges, wallet?.max, wallet?.intervalSeconds]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);

  const remaining =
    baseMs.current == null ? null : Math.max(0, baseMs.current - (now - baseAt.current));

  useEffect(() => {
    if (!wallet || wallet.charges >= wallet.max) return;
    if (remaining == null || remaining > 0) return;
    if (syncingRef.current) return;

    // Anti-spam si le serveur renvoie encore 0 ms.
    const nowTs = Date.now();
    if (nowTs - lastAppliedRegenAt.current < 400) return;
    lastAppliedRegenAt.current = nowTs;
    syncingRef.current = true;

    const nextCharges = Math.min(wallet.max, wallet.charges + 1);
    const optimisticMs = nextCharges >= wallet.max ? null : Math.max(1000, wallet.intervalSeconds * 1000);
    const optimistic: Wallet = {
      ...wallet,
      charges: nextCharges,
      msUntilNext: optimisticMs,
    };
    setWallet(optimistic);

    let cancelled = false;
    void (async () => {
      try {
        const me = await api.me();
        if (cancelled) return;
        if (me.zones) setZones(me.zones);
        if (!me.wallet) return;

        const server = me.wallet;
        // Serveur en avance → on le suit.
        if (server.charges > optimistic.charges) {
          setWallet(server);
          return;
        }
        // Serveur encore à 0 ms (ou en retard) → garder le compte à rebours local.
        const serverMs = server.msUntilNext;
        if (
          server.charges <= optimistic.charges &&
          server.charges < server.max &&
          (serverMs == null || serverMs <= 0)
        ) {
          setWallet({
            ...server,
            charges: Math.max(server.charges, optimistic.charges),
            msUntilNext:
              optimistic.msUntilNext ??
              Math.max(1000, server.intervalSeconds * 1000),
          });
          return;
        }
        setWallet(server);
      } catch {
        /* garder l’optimiste */
      } finally {
        syncingRef.current = false;
        // Relance un tick si on était resté à 0 pendant le sync.
        setNow(Date.now());
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [remaining, wallet, setWallet, setZones]);

  return remaining;
}
